/**
 * Skill（技能模板）服务
 * 用于获取可用 skill 列表、创建 skill 图片任务等
 */
import { post, get } from '@/utils';
import { ApiResponse } from '@/utils';
import type { ImageTaskParams, TaskCreateResponse, CreditCalculationResponse } from '../types';
import type { CreateSkillParams, SkillOutputType } from '@/features/expertSkillSquare/types/createSkill';
import type { PublishSkillParams } from '@/features/expertSkillSquare/types/publishSkill';

// ─── 类型定义 ───────────────────────────────────────────────────────────────

/** 后端返回的原始 Skill 数据（/v1/agent/skill/list 接口返回格式） */
export interface SkillTeamRaw {
  skill_team_id: string;
  skill_team_name: string;
  description: string;
  notice: string;
  max_ref_images_count: number;
  skill_team_sub_task_count: number;
}

/** Skill 列表原始响应 data */
export interface SkillListRawResponse {
  list: SkillTeamRaw[];
}

/** /v1/agent/skill/list2 专家团原始项 */
export interface ExpertGroupRaw {
  id: string;
  /** 模板分组 ID（点击「使用」时作为 /canvas/template/copy 入参） */
  g_id?: string;
  name: string;
  description: string;
  notice: string;
  /** 最少参考图数量 */
  min_ref_images_count?: number;
  /** 最多参考图数量 */
  max_ref_images_count?: number;
  skill_team_sub_task_count: number;
  icon_url: string;
  tags: string;
  type: 'text' | 'image' | string;
  usage_method?: string;
  models?: {
    list: Array<{ model_id: string; model_name: string }>;
  };
  expert_list?: Array<{
    name: string;
    nickname: string;
    icon_url: string;
  }>;
  /** 使用次数 */
  use_count?: number;
  /** 收藏数 */
  favorite_count?: number;
  /** 是否已收藏 */
  is_favorite?: boolean;
}

/** /v1/agent/skill/list2 专家原始项 */
export interface ExpertRaw {
  id: string;
  /** 模板分组 ID（点击「使用」时作为 /canvas/template/copy 入参） */
  g_id?: string;
  description: string;
  notice: string;
  tags: string;
  icon_url: string;
  name: string;
  nickname: string;
  type: 'text' | 'image' | string;
  usage_method?: string;
  /** 最少参考图数量 */
  min_ref_images_count?: number;
  /** 最多参考图数量 */
  max_ref_images_count?: number;
  models?: Array<{
    model_id: string;
    model_name: string;
    model_type: string;
    quality: string;
    resolution: string;
    image_ratio: string;
    image_count: number;
  }>;
  /** 使用次数 */
  use_count?: number;
  /** 收藏数 */
  favorite_count?: number;
  /** 是否已收藏 */
  is_favorite?: boolean;
}

/** /v1/agent/skill/list2 分类包装 */
export interface ExpertSkillCategoryGroup<T> {
  category_key: string;
  category_name: string;
  list: T[];
}

/** /v1/agent/skill/list2 skill_list 原始项（技能） */
export interface SkillRaw {
  /** 技能 ID */
  id: string;
  /** 模板分组 ID（点击「使用」时作为 /canvas/template/copy 入参） */
  g_id?: string;
  /** 技能标题 */
  name: string;
  /** 技能分类，可选值：text；image */
  type: 'text' | 'image' | string;
  /** 技能图标 URL */
  icon_url: string;
  /** 作者昵称 */
  nickname: string;
  /** 技能标签（逗号分隔） */
  tags: string;
  /** 使用次数 */
  use_count: number;
  /** 收藏数 */
  favorite_count: number;
  /** 是否已收藏 */
  is_favorite: boolean;
  /** 技能介绍 */
  description: string;
  /** 使用场景 */
  usage_scene: string;
  /** 使用方法 */
  usage_method: string;
  /** 输出内容说明 */
  output_content: string;
  /** 作者昵称（注意：后端字段名为 nikename） */
  nikename: string;
  /** 作者头像 URL（可能为空，卡片空值时用渐变占位） */
  author_icon_url?: string;
  /** Skill 对应 JSON 文件的 OSS 地址（仅 my_skill_list 自建项返回，编辑回填正文用） */
  json_url?: string;
  /**
   * 自建 Skill 状态码（仅 my_skill_list 返回）：
   * 1 未公开 / 2 处理中 / 3 校验未通过 / 4 审核中 / 5 已驳回 / 6 已发布。
   * 解析见 `features/expertSkillSquare/utils/publishStatus.ts`。
   */
  status?: number | string;
  /** 状态文案（后端附带的可读描述，前端以 status 码值为准，此字段仅备查） */
  status_desc?: string;
  /** 运营填写的驳回原因（已驳回态展示，字段名待后端定稿，解析器已做候选名兼容） */
  reject_reason?: string;
  /** 已选开放方式：0=免费开放 1=与平台合作 */
  publish_type?: number;
}

/** /v1/agent/skill/list2 skill_expert 执行专家（text / image 各一） */
export interface SkillExpertRaw {
  /** 专家 ID */
  id: string;
  /** 最小参考图数量 */
  min_ref_images_count: number;
  /** 最大参考图数量 */
  max_ref_images_count: number;
  /** 子任务数量 */
  skill_team_sub_task_count: number;
  /** 模型列表 */
  model: Array<{
    model_id: string;
    model_name: string;
    model_type: 'text' | 'image' | string;
    quality: 'low' | 'medium' | 'high' | string;
    resolution: string;
    image_ratio: string;
    image_count: number;
  }>;
}

/** /v1/agent/skill/list2 原始响应 data */
export interface ExpertSkillList2RawResponse {
  expert_group_list: ExpertSkillCategoryGroup<ExpertGroupRaw>[];
  expert_list: ExpertSkillCategoryGroup<ExpertRaw>[];
  /** 技能列表（对应 Skills Tab 的「通用」）*/
  skill_list?: ExpertSkillCategoryGroup<SkillRaw>[];
  /** 收藏的技能列表（对应 Skills Tab 的「收藏」，内层 skill_list 结构同外层）*/
  favorite_skill_list?: {
    skill_list?: ExpertSkillCategoryGroup<SkillRaw>[];
  };
  /** 自建 Skill 列表（对应 Skills Tab 的「我的」，项内含 json_url）*/
  my_skill_list?: ExpertSkillCategoryGroup<SkillRaw>[];
  /** 文本 / 图片 Skill 执行专家（分类下所有 Skill 共用） */
  skill_expert?: {
    text: SkillExpertRaw;
    image: SkillExpertRaw;
  };
}

/** GET /v1/agent/category/list 分类项 */
export interface CategoryItem {
  /** 分类 code（列表接口作 code 入参） */
  code: string;
  /** 分类展示名 */
  name: string;
}

/** GET /v1/agent/category/list 原始响应 data（各 Tab 的分类） */
export interface CategoryListRawResponse {
  /** 专家团分类 */
  group: { category_list: CategoryItem[] };
  /** 专家分类 */
  expert: { category_list: CategoryItem[] };
  /** Skill 分类（按 通用/收藏/我的 三个子 Tab 分开） */
  skill: {
    public: { category_list: CategoryItem[] };
    my_favorite: { category_list: CategoryItem[] };
    my_skill: { category_list: CategoryItem[] };
  };
}

/** GET /v1/agent/provider/list 列表项（group/expert/skill 字段并集，唯一标识为 code，与旧 id 同值） */
export interface ProviderItemRaw {
  /** 唯一标识（= 旧接口的 id） */
  code: string;
  /** 所属分类 code（分类筛选回传 provider/list 的 code 入参） */
  category_code?: string;
  /** 所属分类名（卡片 / 详情展示） */
  category_name?: string;
  /** 模板分组 ID（点击「使用」时作为 /canvas/template/copy 入参） */
  g_id?: string;
  name: string;
  description: string;
  notice: string;
  usage_method?: string;
  min_ref_images_count?: number;
  max_ref_images_count?: number;
  skill_team_sub_task_count?: number;
  icon_url: string;
  tags: string;
  type: 'text' | 'image' | string;
  use_count?: number;
  favorite_count?: number;
  is_favorite?: boolean;
  /** 模型列表：group 为 { list: [...] }，expert 为平铺数组，取用时需归一化 */
  models?:
    | { list: Array<{ model_id: string; model_name: string }> }
    | Array<{ model_id: string; model_name: string; model_type?: string }>;
  /** 专家团成员（仅 group） */
  expert_list?: Array<{
    name: string;
    nickname: string;
    icon_url: string;
  }>;
  /* ── skill 专属 ── */
  /** 作者昵称 */
  nickname?: string;
  /** 作者头像 URL */
  author_icon_url?: string;
  /** 使用场景 */
  usage_scene?: string;
  /** 输出内容说明 */
  output_content?: string;
  /** Skill 正文 JSON 的 OSS 地址（仅 my_skill 自建项返回） */
  json_url?: string;
  /** 自建 Skill 状态码（仅 my_skill 返回），解析见 `features/expertSkillSquare/utils/publishStatus.ts` */
  status?: number | string;
  status_desc?: string;
  reject_reason?: string;
  is_editable?: boolean;
  is_deletable?: boolean;
  /** 已选开放方式：0=免费开放 1=与平台合作（仅 my_skill 返回，发布弹窗回填用） */
  publish_type?: number;
}

/** GET /v1/agent/provider/list 请求参数 */
export interface ProviderListParams {
  /** 执行目标：group=专家团，expert=专家，skill=Skill（必传） */
  execute_target: 'group' | 'expert' | 'skill';
  /** text / image，不传返回全部 */
  task_type?: 'text' | 'image' | string;
  /** 分类 code，不传返回全部 */
  code?: string;
  /** public=通用，my_favorite=收藏，my_skill=我的（仅 execute_target=skill 时有效，默认 public） */
  skill_type?: 'public' | 'my_favorite' | 'my_skill';
  /** 画布传 'canvas'，不传默认为空 */
  filter?: string;
  /** 名称模糊搜索（专家/专家团/Skill 通用） */
  name?: string;
  page_number: number;
  page_size: number;
}

/** GET /v1/agent/provider/list 原始响应 data */
export interface ProviderListRawResponse {
  list: ProviderItemRaw[];
  page_number: number;
  page_size: number;
  total: number;
}

/** POST /v1/agent/skill/detail 返回 data（自建 Skill 详情，编辑回填用） */
export interface SkillDetailResponse {
  /** 技能标题 */
  name: string;
  /** 技能介绍 */
  description: string;
  /** 输出结果类型：text/image/video/audio */
  output_type: SkillOutputType;
  /** 封面图 URL（选填） */
  icon_url?: string;
  /** Skill 对应 JSON 文件的 OSS 地址（正文文件树载体） */
  json_url: string;
  /** 使用场景 */
  usage_scene: string;
  /** 如何使用 */
  usage_method: string;
  /** 输出结果说明 */
  output_content: string;
}

/** Skill 图片任务创建参数（继承通用图片参数 + execute_target + skill_team_id / expert_id 二者必选其一） */
export interface SkillImageTaskParams extends ImageTaskParams {
  /** 执行目标：group=专家团，expert=专家，skill=Skill */
  execute_target: 'group' | 'expert' | 'skill';
  /** 专家团 ID（选择专家团时必填） */
  skill_team_id?: string;
  /** 专家 ID（选择专家时必填） */
  expert_id?: string;
  /** Skill ID 列表（选择 Skill 时必填，支持多个 skill） */
  skill_ids?: string[];
}

/** Skill 米粒值计算参数 */
export interface SkillCreditsParams {
  model_id: string;
  /** 执行目标：group=专家团，expert=专家，skill=Skill */
  execute_target?: 'group' | 'expert' | 'skill';
  /** 专家团 ID（选择专家团时必填） */
  skill_team_id?: string;
  /** 专家 ID（选择专家时必填） */
  expert_id?: string;
  /** Skill ID 列表（选择 Skill 时必填，支持多个 skill） */
  skill_ids?: string[];
}

/** Skill 子任务项 */
export interface SkillSubtask {
  msg_iid: string;
  task_status: number;
  /** 文本类型 Skill 子任务完成后返回的文本结果（如脚本、提示词等） */
  text_msg?: string;
}

/** Skill 子任务查询响应 */
export interface SkillTaskResponse {
  task_status: number;
  subtask: SkillSubtask[];
  /** 意图识别失败等场景下的拒绝原因文案 */
  reject_message?: string;
}

/** Skill 子任务详情查询参数 */
export interface SkillTaskDetailParams {
  parent_msg_iid: string;
  sub_task_id: string;
}

/** Skill 子任务详情参考图项 */
export interface SkillTaskDetailRefImage {
  file_id: string;
  url: string;
}

/** Skill 子任务详情响应 */
export interface SkillTaskDetailResponse {
  run_id: string;
  parent_msg_iid: string;
  sub_task_id: string;
  sub_task_name: string;
  expert_code: string;
  expert_name: string;
  expert_type: string;
  status: string;
  msg_iid: string;
  model_id: string;
  image_size: string;
  prompt: string;
  reference_images: SkillTaskDetailRefImage[];
  result_images: string[];
  parameter_overrides: {
    height?: number;
    image_ratio?: string;
    number?: number;
    prompt?: string;
    ref_images?: number;
    resolution?: string;
    width?: number;
  };
}

/**
 * AMAI Skill 服务
 */
class SkillService {
  /**
   * 获取可用 Skill 列表
   * @returns Skill 原始列表
   */
  async getSkillList(): Promise<ApiResponse<SkillListRawResponse>> {
    return get<SkillListRawResponse>('/agent/skill/list');
  }

  /**
   * 获取文本节点专家&Skills 列表（含专家团 + 专家）
   * @param params.filter 场景过滤：canvas=画布页面调用，仅返回创建成功（校验通过）的自建 Skill
   * @returns 专家团与专家分类列表
   */
  async getExpertSkillList(params?: { filter?: 'canvas' | string }): Promise<ApiResponse<ExpertSkillList2RawResponse>> {
    return get<ExpertSkillList2RawResponse>('/agent/skill/list2', params);
  }

  /**
   * 获取各 Tab 的分类列表（画布新版，配合 getProviderList 替代 list2）
   * @param params.task_type 文本节点传 text、图片节点传 image，不传默认展示全部
   * @param params.filter 画布传 'canvas'，不传默认为空
   */
  async getCategoryList(params?: { task_type?: 'text' | 'image' | string; filter?: string }): Promise<ApiResponse<CategoryListRawResponse>> {
    return post<CategoryListRawResponse>('/agent/category/list', params);
  }

  /**
   * 按分类分页查询专家团/专家/Skill 列表（画布新版）
   * 列表项唯一标识为 code（与旧接口 id 同值）
   */
  async getProviderList(params: ProviderListParams): Promise<ApiResponse<ProviderListRawResponse>> {
    return post<ProviderListRawResponse>('/agent/provider/list', params);
  }

  /**
   * 创建 Skill 图片生成任务
   * @param params 任务参数（含 skill_team_id）
   * @returns 任务创建结果，包含任务ID (msg_iid) 或父任务ID (parent_msg_iid)
   */
  async createSkillImageTask(params: SkillImageTaskParams): Promise<ApiResponse<TaskCreateResponse>> {
    return post<TaskCreateResponse>('/agent/skill/image/task', params);
  }

  /**
   * 查询 Skill 子任务列表
   * @param parent_msg_iid 父任务ID
   * @param expert_type 专家类型：image / text
   * @returns 子任务状态列表
   */
  async querySkillTask(parent_msg_iid: string, expert_type?: 'image' | 'text'): Promise<ApiResponse<SkillTaskResponse>> {
    return get<SkillTaskResponse>('/agent/skill/task', { parent_msg_iid, expert_type });
  }

  /**
   * 查询 Skill 子任务详情（含提示词、参考图、结果图等）
   * @param params 父任务ID + 子任务ID
   * @returns 子任务完整详情
   */
  async querySkillTaskDetail(params: SkillTaskDetailParams): Promise<ApiResponse<SkillTaskDetailResponse>> {
    return get<SkillTaskDetailResponse>('/agent/skill/task/detail', { ...params });
  }

  /**
   * 计算 Skill 图片生成任务所需点数
   * @param params 任务参数（含 skill_team_id）
   * @returns 计算结果，包含总消耗点数和用户剩余点数
   */
  async calculateSkillCredits(params: SkillCreditsParams): Promise<CreditCalculationResponse> {
    return post('/agent/skill/credits', params) as any;
  }

  /**
   * 收藏/取消收藏 Skill / 专家 / 专家团
   * @param params action=1 收藏, action=2 取消; id_type=1 专家团, 2 专家, 3 skill; id=收藏对象id
   */
  async bookmarkSkill(params: { action: 1 | 2; id_type: 1 | 2 | 3; id: string }): Promise<ApiResponse<{ result: boolean }>> {
    return post('/canvas/bookmark', params) as any;
  }

  /**
   * 创建私有 Skill（对齐《接口文档.md》POST /v1/agent/skill/create，§3.4.6）。
   * 后端触发风控校验：code===0 视为通过并落库；非 0 取 message 作未通过提示。
   * 封面(icon_url)、正文(json_url)均为上传 OSS 后的地址，由调用方先行上传换取。
   * @param params 基础信息 + 封面/正文 URL + 使用说明
   */
  async createSkill(params: CreateSkillParams): Promise<ApiResponse<{ id: string }>> {
    return post('/agent/skill/create', params) as any;
  }

  /**
   * 修改私有 Skill（对齐《接口文档.md》POST /v1/agent/skill/update）。
   * 入参同 create，另需带 id；后端同样触发风控，code===0 视为通过。
   */
  async updateSkill(params: CreateSkillParams & { id: string }): Promise<ApiResponse<{ id?: string }>> {
    return post('/agent/skill/update', params) as any;
  }

  /**
   * 查看私有 Skill 详情（对齐《接口文档.md》POST /v1/agent/skill/detail）。
   * 返回创建时全字段，含 json_url，供编辑弹窗回填正文文件树。
   */
  async getSkillDetail(id: string): Promise<ApiResponse<SkillDetailResponse>> {
    return post('/agent/skill/detail', { id }) as any;
  }

  /**
   * 删除私有 Skill（对齐《接口文档.md》POST /v1/agent/skill/delete）。
   */
  async deleteSkill(id: string): Promise<ApiResponse<{}>> {
    return post('/agent/skill/delete', { id }) as any;
  }

  /**
   * 发布（公开）私有 Skill（对齐《接口文档.md》POST /v1/agent/skill/publish，§3.5.4）。
   * 提交成功即进入运营审核队列，前端状态置「审核中」；已发布 Skill 编辑后重提走同一接口（二次审核）。
   * @param params id=技能 ID；publish_type=开放方式（0 免费开放 / 1 与平台合作）
   */
  async publishSkill(params: PublishSkillParams): Promise<ApiResponse<{}>> {
    return post('/agent/skill/publish', params) as any;
  }

  /**
   * 取消公开私有 Skill（对齐《接口文档.md》POST /v1/agent/skill/unpublish，§3.5.9）。
   * 成功后从 Skill 广场下架、回到仅自己可见；创作者仍可自用与再次发起发布。
   */
  async unpublishSkill(id: string): Promise<ApiResponse<{}>> {
    return post('/agent/skill/unpublish', { id }) as any;
  }
}

export default new SkillService();
